import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Send, Trash2, Check } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Section } from "@/admin/hr/common";

export function NotesPanel({ coll, item, onSaved }) {
  const [text, setText] = useState("");
  const save = async () => {
    if (!text.trim()) return;
    try {
      const r = await api.post(`/admin/hr/${coll}/${item.id}/notes`, { text });
      setText("");
      onSaved(r.data);
      toast.success("Not eklendi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Section title="Yönetici notları" testid="notes-panel">
      <ul className="mb-3 max-h-48 space-y-2 overflow-y-auto">
        {(item.notes || []).slice().reverse().map((n) => (
          <li key={n.id} className="rounded-md bg-zinc-50 p-2 text-sm"><p className="whitespace-pre-wrap text-zinc-900">{n.text}</p><p className="mt-1 text-[11px] text-zinc-500">{fmtDate(n.at)} · {n.by}</p></li>
        ))}
        {!item.notes?.length && <li className="text-sm text-zinc-500">Henüz not yok.</li>}
      </ul>
      <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} className="field-input" placeholder="Not yazın..." data-testid="note-input" />
      <button onClick={save} className="btn-outline mt-2 !py-2" data-testid="note-save-btn">Notu Ekle</button>
    </Section>
  );
}

export function ReminderPanel({ coll, item }) {
  const [list, setList] = useState([]);
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const load = () => api.get("/admin/hr/reminders", { params: { ref_id: item.id } }).then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!due) return toast.error("Tarih ve saat seçin");
    try {
      await api.post("/admin/hr/reminders", { ref_coll: coll, ref_id: item.id, due_at: due, note });
      setDue("");
      setNote("");
      load();
      toast.success("Hatırlatma oluşturuldu");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const done = async (id) => {
    await api.patch(`/admin/hr/reminders/${id}`);
    load();
  };
  return (
    <Section title="Geri arama hatırlatması" testid="reminder-panel">
      {list.map((r) => (
        <div key={r.id} className="mb-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 p-2 text-sm">
          <span><Bell className="mr-1 inline h-3.5 w-3.5 text-amber-700" />{new Date(r.due_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })} {r.note && `· ${r.note}`}</span>
          <button onClick={() => done(r.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid={`reminder-done-${r.id}`}><Check className="h-3.5 w-3.5" /> Tamamlandı</button>
        </div>
      ))}
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="field-input" data-testid="reminder-due" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Kısa not" maxLength={300} className="field-input" data-testid="reminder-note" />
        <button onClick={add} className="btn-outline !py-2" data-testid="reminder-add-btn">Ekle</button>
      </div>
    </Section>
  );
}

export function MailPanel({ coll, item, onSent }) {
  const [subject, setSubject] = useState("Rüzgar Özel Güvenlik – Başvurunuz hakkında");
  const [body, setBody] = useState(`Sayın ${item.name},\n\n`);
  const [busy, setBusy] = useState(false);
  if (!item.email) return null;
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/hr/${coll}/${item.id}/email`, { subject, body });
      toast.success(`E-posta gönderildi: ${item.email}`);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title={`E-posta gönder (${item.email})`} testid="mail-panel">
      <input value={subject} onChange={(e) => setSubject(e.target.value)} className="field-input mb-2" data-testid="mail-subject" />
      <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} className="field-input" data-testid="mail-body" />
      <button disabled={busy} onClick={send} className="btn-red mt-2 !py-2 disabled:opacity-60" data-testid="mail-send-btn"><Send className="h-4 w-4" /> Gönder</button>
    </Section>
  );
}

export function DeletePanel({ coll, item, no, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const del = async () => {
    try {
      await api.delete(`/admin/hr/${coll}/${item.id}`, { params: { confirm } });
      toast.success("Kayıt silindi");
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Section title="Kaydı sil" testid="delete-panel">
      <p className="mb-2 text-xs text-zinc-600">Kişisel veriler ve belgeler kalıcı olarak silinir. Onaylamak için kayıt numarasını (<b>{no}</b>) yazın.</p>
      <div className="flex gap-2">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="field-input" placeholder={no} data-testid="delete-confirm-input" />
        <button disabled={confirm !== no} onClick={del} className="inline-flex shrink-0 items-center gap-1 rounded-md bg-red-600 px-3 text-sm font-semibold text-white disabled:opacity-40" data-testid="delete-confirm-btn"><Trash2 className="h-4 w-4" /> Sil</button>
      </div>
    </Section>
  );
}
